/* Points — retail locations, assignment and data quality.
 *
 * 1:1 with `docs/design/points.html`. This is the bespoke list the descriptor-driven
 * `ResourceTable` was always the interim for (see the points-framework memory): the mockup's
 * angle is DATA QUALITY, and two states that break operations on their own are first-class
 * filters rather than columns the user has to eyeball —
 *   • no coordinates → the point cannot be geofenced, so a visit to it can never be verified
 *   • unassigned (no responsible merchandiser) → nobody is going.
 *
 * Built the same way `/visit` and `/supervisors` are (spec/03 archetype A): the shared `lp-*`
 * chrome (`component/list-page.scss`), `table-cells` primitives, `EntitySelect`,
 * `FilterChips`/`useLabelCache`, and the same table / footer / bulk-bar composition. The only
 * new code is the columns, the four extra filters and the archive lifecycle. The other eight
 * admin entities still render through the generic `ResourceTable`.
 *
 * Everything narrows on the SERVER — `GET points/` gained the data-quality filters, the
 * activity annotation (`last_visit`) in the same change
 * (picsell-api `points/api/web/v1/`).
 *
 * ⚠ Archived (`is_removed`, via DELETE) is NOT the editable `is_active` flag shown beside it —
 * softDelete semantics. Row click opens a quick-look modal; "Open editor" navigates to the full
 * `/points/:id` page, which is still the generic `ResourcePage`. */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Avatar, Button, Card, Col, Empty, Flex, Form, Input, Modal, Row, Segmented, Space, Table, Tag, Tooltip, Typography } from "antd";
import {
  ApartmentOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  CompassOutlined,
  EditOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  InboxOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  StopOutlined,
  TeamOutlined,
  UndoOutlined,
  UserDeleteOutlined,
} from "@ant-design/icons";

import Alert from "../../util/alert";

import EntitySelect from "../../component/EntitySelect";
import FilterChips, { useLabelCache } from "../../component/FilterChips";
import { PersonCell, RowActions, TableFoot, TitleCell, actionsColumn, dash, initialsOf, useTableScrollY } from "../../component/table-cells";
import AddToGroup from "./ext/AddToGroup";
import { ENTITY_DROPDOWN_SETTINGS, PHOTO_REPORT, POINTS_ADMIN, VISIT_PAGE } from "../../config/lang";
import { call, getAbortable } from "../../util/action";
import { fill, regionId, translate } from "../../util/util";

import "./points-list.scss";

const PAGE_SIZES = [25, 50, 100];

const SEGMENTS = [
  ["all", "seg_all"],
  ["active", "active"],
  ["inactive", "inactive"],
  ["archived", "archived"],
];

/* Column key → the field the API orders on. A key missing here is not sortable (spec/03). */
const ORDERING = {
  name: "name",
  network: "network__name",
  location: "locality__name",
  assignment: "responsible_merchandiser__first_name",
  last_visit: "last_visit",
  minutes: "minutes_recommended",
  status: "is_active",
};

const FIELD_COL = { xs: { flex: "0 0 100%" }, sm: { flex: "0 0 50%" }, xl: { flex: "0 0 33.3333%" } };

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);
const netName = row => row?.network?.name || "";
/* A deterministic hue per network, so the logo stand-in is stable across renders — the mockup's
   `netHue`. Only used when a network has no uploaded logo. */
const netHue = row => ((idOf(row?.network) || 0) * 47 + 190) % 360;

/** The request the table runs on — one place where the endpoint's parameter shape is visible. */
function buildQuery({ search, network, county, locality, category, merch, segment, noGeo, noMerch, page, size, sort }) {
  const p = new URLSearchParams();
  p.set("page", String(page));
  p.set("page_size", String(size));
  if (search) p.set("search", search);
  if (network) p.set("network", String(idOf(network)));
  if (county) p.set("locality__county", String(idOf(county)));
  if (locality) p.set("locality", String(idOf(locality)));
  if (category) p.set("category", String(idOf(category)));
  if (merch) p.set("responsible_merchandiser", String(idOf(merch)));
  p.set("archived", segment === "archived" ? "true" : "false");
  if (segment === "active") p.set("is_active", "true");
  if (segment === "inactive") p.set("is_active", "false");
  if (noGeo) p.set("no_geo", "true");
  if (noMerch) p.set("unassigned", "true");
  if (sort) p.set("ordering", `${sort.dir === "descend" ? "-" : ""}${ORDERING[sort.key]}`);
  return p.toString();
}

/** URL ↔ state, so a filtered view is shareable and survives Back from a point's editor. */
function stateToSearch(s) {
  const p = new URLSearchParams();
  if (s.search) p.set("q", s.search);
  ["network", "county", "locality", "category", "merch"].forEach(key => {
    if (s[key]) p.set(key, String(idOf(s[key])));
  });
  if (s.segment !== "all") p.set("segment", s.segment);
  if (s.noGeo) p.set("no_geo", "1");
  if (s.noMerch) p.set("unassigned", "1");
  if (s.page > 1) p.set("page", String(s.page));
  if (s.size !== PAGE_SIZES[0]) p.set("size", String(s.size));
  if (s.sort) p.set("sort", `${s.sort.dir === "descend" ? "-" : ""}${s.sort.key}`);
  return p.toString();
}

function searchToState(locationSearch, me) {
  const p = new URLSearchParams(locationSearch);
  const sort = p.get("sort");
  const hasFilters = Boolean(locationSearch && locationSearch.replace(/^\?/, ""));
  return {
    search: p.get("q") || "",
    network: p.get("network") || "",
    /* Without a URL to restore, the manager's own region is the sensible default scope — the
       same seeding the visits and supervisors lists do. */
    county: p.get("county") || (hasFilters ? "" : regionId(me) || ""),
    locality: p.get("locality") || "",
    category: p.get("category") || "",
    merch: p.get("merch") || "",
    segment: SEGMENTS.some(([value]) => value === p.get("segment")) ? p.get("segment") : "all",
    noGeo: p.get("no_geo") === "1",
    noMerch: p.get("unassigned") === "1",
    page: Math.max(1, Number(p.get("page")) || 1),
    size: PAGE_SIZES.includes(Number(p.get("size"))) ? Number(p.get("size")) : PAGE_SIZES[0],
    sort: sort && ORDERING[sort.replace(/^-/, "")] ? { key: sort.replace(/^-/, ""), dir: sort[0] === "-" ? "descend" : "ascend" } : null,
  };
}

const List = () => {
  const history = useHistory();
  const location = useLocation();
  const me = useSelector(state => state.me);
  const lang = me?.language || "en";

  const t = key => translate(POINTS_ADMIN, key, lang);
  const td = key => translate(ENTITY_DROPDOWN_SETTINGS, key, lang);
  const tp = key => translate(PHOTO_REPORT, key, lang);
  const tv = key => translate(VISIT_PAGE, key, lang);

  const initial = useRef(searchToState(location.search, me)).current;

  const [search, setSearch] = useState(initial.search);
  const [typed, setTyped] = useState(initial.search);
  const [network, setNetwork] = useState(initial.network);
  const [county, setCounty] = useState(initial.county);
  const [locality, setLocality] = useState(initial.locality);
  const [category, setCategory] = useState(initial.category);
  const [merch, setMerch] = useState(initial.merch);
  const [segment, setSegment] = useState(initial.segment);
  const [noGeo, setNoGeo] = useState(initial.noGeo);
  const [noMerch, setNoMerch] = useState(initial.noMerch);
  const [page, setPage] = useState(initial.page);
  const [size, setSize] = useState(initial.size);
  const [sort, setSort] = useState(initial.sort);

  /* Option objects the selects handed back, so a chosen value labels its own chip. */
  const [chosen, setChosen] = useState({ network: null, county: null, locality: null, category: null, merch: null });
  const rememberChosen = (key, option) => setChosen(prev => ({ ...prev, [key]: option }));

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  /* --------------------------------------------------------------- fetch */

  const query = useMemo(() => buildQuery({ search, network, county, locality, category, merch, segment, noGeo, noMerch, page, size, sort }), [
    search,
    network,
    county,
    locality,
    category,
    merch,
    segment,
    noGeo,
    noMerch,
    page,
    size,
    sort,
  ]);

  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey(k => k + 1);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getAbortable(`points/?${query}`, controller.signal)
      .then(response => {
        if (!response) return;
        setRows(response.results || []);
        setTotal(response.count || 0);
        setLoading(false);
      })
      .catch(error => {
        if (error.name === "AbortError") return;
        setRows([]);
        setTotal(0);
        setLoading(false);
      });
    return () => controller.abort();
  }, [query, reloadKey]);

  /* 250ms, matching every other search box in the app: one request per pause, not per keystroke. */
  useEffect(() => {
    if (typed === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(typed);
      setPage(1);
      setSelected([]);
    }, 250);
    return () => clearTimeout(timer);
  }, [typed, search]);

  useEffect(() => {
    const next = stateToSearch({ search, network, county, locality, category, merch, segment, noGeo, noMerch, page, size, sort });
    if (next !== location.search.replace(/^\?/, "")) history.replace({ search: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, network, county, locality, category, merch, segment, noGeo, noMerch, page, size, sort]);

  /* Any narrowing invalidates the page number: page 7 of the old result set is usually past the
     end of the new one, which renders an empty table over a non-empty result. */
  const narrow = fn => (...args) => {
    setPage(1);
    setSelected([]);
    fn(...args);
  };

  const resetFilters = narrow(() => {
    setTyped("");
    setSearch("");
    setNetwork("");
    setCounty("");
    setLocality("");
    setCategory("");
    setMerch("");
    setChosen({ network: null, county: null, locality: null, category: null, merch: null });
    setSegment("all");
    setNoGeo(false);
    setNoMerch(false);
    setSort(null);
    Alert.info(t("filters_reset"), { position: "top-right" });
  });

  /* ---------------------------------------------------------------- chips */

  const { labels, remember, nameFor } = useLabelCache();

  const chips = useMemo(() => {
    const out = [];
    if (search) out.push({ key: "q", label: tp("search") || t("search_ph"), value: search });
    if (network) out.push({ key: "network", label: t("f_network"), value: nameFor("network", chosen.network ?? network) });
    if (county) out.push({ key: "county", label: t("f_county"), value: nameFor("county", chosen.county ?? county) });
    if (locality) out.push({ key: "locality", label: t("f_locality"), value: nameFor("locality", chosen.locality ?? locality) });
    if (category) out.push({ key: "category", label: t("f_category"), value: nameFor("category", chosen.category ?? category) });
    if (merch) out.push({ key: "merch", label: t("f_merchandiser"), value: nameFor("merch", chosen.merch ?? merch) });
    if (noGeo) out.push({ key: "no_geo", label: t("data_quality"), value: t("no_coords") });
    if (noMerch) out.push({ key: "unassigned", label: t("data_quality"), value: t("unassigned") });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, network, county, locality, category, merch, noGeo, noMerch, chosen, labels, lang]);

  const clearChip = narrow(key => {
    if (key === "q") {
      setTyped("");
      setSearch("");
    } else if (key === "no_geo") {
      setNoGeo(false);
    } else if (key === "unassigned") {
      setNoMerch(false);
    } else {
      /* One of the entity selects. */
      const setter = { network: setNetwork, county: setCounty, locality: setLocality, category: setCategory, merch: setMerch }[key];
      setter("");
      rememberChosen(key, null);
      /* Clearing the county unscopes and clears the locality with it. */
      if (key === "county") {
        setLocality("");
        rememberChosen("locality", null);
      }
    }
  });

  /* ------------------------------------------------------------- archive / restore */

  const archiveOne = (row, restore) =>
    new Promise(resolve => {
      if (restore) call({ method: "PATCH", url: `points/${row.id}/`, post: { is_removed: false } }, r => resolve(Boolean(r)));
      else call({ method: "DELETE", url: `points/${row.id}/` }, r => resolve(Boolean(r) && r.status === 204));
    });

  const runArchive = async (targets, restore) => {
    if (!targets.length) {
      setSelected([]);
      return;
    }
    setBusy(true);
    const results = await Promise.all(targets.map(row => archiveOne(row, restore)));
    setBusy(false);
    const done = results.filter(Boolean).length;
    if (done) {
      Alert.success(fill(t(restore ? "restored_n" : "archived_n"), { n: done }), { position: "top-right" });
      reload();
    }
    if (done < targets.length) {
      Alert.warning(fill(t("bulk_failed_n"), { n: targets.length - done }), { position: "top-right" });
    }
    setSelected([]);
  };

  /* Archiving is destructive, so it is confirmed and the confirm says how many (spec/03).
     Restore is not, so it just runs. `Modal.confirm` is a static call — it renders outside the
     tree and does not inherit AppShell's `ConfigProvider locale`, so the buttons are labelled
     explicitly or they stay English. */
  const confirmArchive = (targets, restore) => {
    if (restore) {
      runArchive(targets, true);
      return;
    }
    Modal.confirm({
      title: fill(t(targets.length === 1 ? "confirm_archive" : "confirm_archive_n"), { n: targets.length }),
      okText: t("archive_btn"),
      okButtonProps: { danger: true },
      cancelText: t("back_btn"),
      onOk: () => runArchive(targets, false),
    });
  };

  const openEditor = row => history.push(`/points/${row.id}`);

  /* ------------------------------------------------------------- columns */

  const columns = useMemo(
    () => [
      {
        key: "name",
        title: t("f_name"),
        width: 300,
        fixed: "left",
        ellipsis: true,
        sorter: true,
        render: (value, row) => (
          <Flex align="center" gap={10} className="pt-id">
            <Avatar shape="square" size={30} className="pt-logo" style={{ "--h": netHue(row), "fontSize": 12 }} src={row.network?.image || null}>
              {initialsOf(netName(row))}
            </Avatar>
            <TitleCell title={row.name} sub={[row.code, row.address].filter(Boolean).join(" · ")} />
          </Flex>
        ),
      },
      {
        key: "network",
        title: t("f_network"),
        width: 170,
        ellipsis: true,
        sorter: true,
        render: (value, row) => <TitleCell title={netName(row) || dash} sub={row.category?.name} />,
      },
      {
        key: "location",
        title: t("f_locality"),
        width: 210,
        ellipsis: true,
        sorter: true,
        render: (value, row) => (
          <TitleCell
            title={row.locality?.name || dash}
            sub={
              row.location ? (
                <span className="pt-geo">
                  <EnvironmentOutlined className="pt-geo-icon" /> {row.location.latitude?.toFixed?.(5)}, {row.location.longitude?.toFixed?.(5)}
                </span>
              ) : (
                <Tooltip title={t("no_coords_hint")}>
                  <Tag className="pt-tag pt-tag--warn" icon={<EnvironmentOutlined />}>
                    {t("no_coords")}
                  </Tag>
                </Tooltip>
              )
            }
          />
        ),
      },
      {
        key: "assignment",
        title: t("f_merchandiser"),
        width: 210,
        ellipsis: true,
        sorter: true,
        render: (value, row) => {
          if (!row.responsible_merchandiser) {
            return (
              <Tag className="pt-tag pt-tag--warn" icon={<UserDeleteOutlined />}>
                {t("unassigned")}
              </Tag>
            );
          }
          const sups = row.responsible_supervisors || [];
          const supNames = sups.map(s => [s.first_name, s.last_name].filter(Boolean).join(" ")).join(", ");
          return (
            <Flex vertical gap={2} className="pt-assign">
              <PersonCell user={row.responsible_merchandiser} strong={false} />
              {sups.length ? (
                <Tooltip title={`${t("f_supervisors")}: ${supNames}`}>
                  <Typography.Text type="secondary" className="pt-sup">
                    <TeamOutlined /> {sups.length}
                  </Typography.Text>
                </Tooltip>
              ) : null}
            </Flex>
          );
        },
      },
      /* Last visit, not a visit COUNT: the count was a `Count` join, so it dragged a GROUP BY
         through every query on the points viewset — including this list's pagination `COUNT(*)`
         and the app-bar search, which borrows the same queryset. The date is one correlated
         subquery and answers "is anyone going there" more directly. */
      {
        key: "last_visit",
        title: t("last_visit"),
        width: 130,
        align: "right",
        sorter: true,
        render: (value, row) =>
          row.last_visit ? (
            <span className="lp-num">
              {new Date(row.last_visit).toLocaleDateString(lang === "en" ? "en-GB" : lang, { day: "2-digit", month: "short", year: "numeric" })}
            </span>
          ) : (
            <Typography.Text type="secondary" className="pt-lastvisit">
              {t("never_visited")}
            </Typography.Text>
          ),
      },
      {
        key: "minutes",
        title: t("min_col"),
        dataIndex: "minutes_recommended",
        width: 116,
        align: "right",
        sorter: true,
        render: value => (
          <span className="lp-num">
            {value || 0} {t("min_short")}
          </span>
        ),
      },
      {
        key: "status",
        title: t("f_status"),
        width: 150,
        render: (value, row) => (
          <Flex vertical align="flex-start" gap={4} className="pt-status">
            {row.is_removed ? (
              <Tag className="pt-tag pt-tag--warn" icon={<InboxOutlined />}>
                {t("archived")}
              </Tag>
            ) : null}
            {row.is_active ? (
              <Tag className="pt-tag pt-tag--ok" icon={<CheckCircleOutlined />}>
                {t("active")}
              </Tag>
            ) : (
              <Tag className="pt-tag pt-tag--off" icon={<MinusCircleOutlined />}>
                {t("inactive")}
              </Tag>
            )}
            {row.is_available_for_task_creation ? null : (
              <Tooltip title={t("f_available_tasks")}>
                <Tag className="pt-tag pt-tag--muted" icon={<StopOutlined />}>
                  {t("no_tasks")}
                </Tag>
              </Tooltip>
            )}
          </Flex>
        ),
      },
      actionsColumn((value, row) => (
        <RowActions
          label={tv("row_actions")}
          items={[
            { key: "open", icon: <EditOutlined />, label: t("open_editor") },
            row.is_removed
              ? { key: "restore", icon: <UndoOutlined />, label: t("restore_btn") }
              : { key: "archive", icon: <InboxOutlined />, label: t("archive_btn"), danger: true },
          ]}
          onClick={key => {
            if (key === "open") openEditor(row);
            else confirmArchive([row], key === "restore");
          }}
        />
      )),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, rows]
  );

  const scrollY = useTableScrollY();

  /* The selected rows themselves — the bulk bar archives them, and "Add to group" needs their
     names, not just their ids. */
  const targets = rows.filter(row => selected.includes(row.id));

  return (
    <Flex vertical gap="middle" className="shell-content lp pt">
      <Flex className="lp-head" justify="space-between" align="flex-end" gap="middle" wrap>
        <Flex vertical>
          <Typography.Title level={1} className="lp-title">
            {t("points_title")}
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="lp-sub">
            {t("points_sub")}
          </Typography.Paragraph>
        </Flex>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => history.push("/points/new")}>
          {t("new_point")}
        </Button>
      </Flex>

      <Card className="lp-filters" variant="outlined">
        <Form layout="vertical" component={false} colon={false}>
          <Row gutter={[12, 12]}>
            <Col {...FIELD_COL}>
              <Form.Item label={tp("search") || t("search_ph")}>
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder={t("search_point_ph")}
                  value={typed}
                  onChange={event => setTyped(event.target.value)}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={t("f_network")}>
                <EntitySelect
                  entity="network"
                  value={network}
                  seed={chosen.network}
                  prefix={<ApartmentOutlined />}
                  onChange={narrow(option => {
                    rememberChosen("network", option);
                    setNetwork(option ? idOf(option) : "");
                  })}
                  onResolve={remember("network")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={t("f_county")}>
                <EntitySelect
                  entity="county"
                  value={county}
                  seed={chosen.county || (me?.county && typeof me.county === "object" ? me.county : null)}
                  prefix={<CompassOutlined />}
                  onChange={narrow(option => {
                    rememberChosen("county", option);
                    setCounty(option ? idOf(option) : "");
                    /* The old locality may not exist in the new county — clear it (mockup). */
                    setLocality("");
                    rememberChosen("locality", null);
                  })}
                  onResolve={remember("county")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={t("f_locality")}>
                <EntitySelect
                  entity="locality"
                  value={locality}
                  seed={chosen.locality}
                  prefix={<EnvironmentOutlined />}
                  extraQuery={county ? { county: idOf(county) } : undefined}
                  onChange={narrow(option => {
                    rememberChosen("locality", option);
                    setLocality(option ? idOf(option) : "");
                  })}
                  onResolve={remember("locality")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={t("f_category")}>
                <EntitySelect
                  entity="categories_point"
                  value={category}
                  seed={chosen.category}
                  prefix={<AppstoreOutlined />}
                  onChange={narrow(option => {
                    rememberChosen("category", option);
                    setCategory(option ? idOf(option) : "");
                  })}
                  onResolve={remember("category")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={t("f_merchandiser")}>
                <EntitySelect
                  entity="merchandiser"
                  value={merch}
                  seed={chosen.merch}
                  prefix={<IdcardOutlined />}
                  onChange={narrow(option => {
                    rememberChosen("merch", option);
                    setMerch(option ? idOf(option) : "");
                  })}
                  onResolve={remember("merch")}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterChips className="lp-chips" items={chips} closeLabel={tp("a_remove_filter")} onClose={clearChip} />

          <Flex className="lp-filters__foot" align="center" gap="small" wrap>
            <Segmented value={segment} onChange={narrow(setSegment)} options={SEGMENTS.map(([value, key]) => ({ value, label: t(key) }))} />
            {/* The two data-quality states, as derived-state filters: neither is a column the
                user can eyeball. */}
            <Tag.CheckableTag className="pt-chip" checked={noGeo} onChange={narrow(setNoGeo)}>
              <Space size={6}>
                <EnvironmentOutlined />
                {t("no_coords")}
              </Space>
            </Tag.CheckableTag>
            <Tag.CheckableTag className="pt-chip" checked={noMerch} onChange={narrow(setNoMerch)}>
              <Space size={6}>
                <UserDeleteOutlined />
                {t("unassigned")}
              </Space>
            </Tag.CheckableTag>
            <span className="lp-spacer" />
            <Button size="small" onClick={resetFilters}>
              {td("filter_reset")}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Card className="lp-table" variant="outlined">
        <Flex className="lp-table__bar" align="center" gap="small" wrap>
          <Typography.Text strong className="lp-table__title">
            {t("points_title")}
          </Typography.Text>
          <Typography.Text type="secondary" aria-live="polite">
            {tp("records_count")}: <Typography.Text strong>{total}</Typography.Text>
          </Typography.Text>
        </Flex>

        <Table
          className="lp-table__grid"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 1180, y: scrollY }}
          rowClassName={row => (selected.includes(row.id) ? "lp-row lp-row--on" : "lp-row")}
          rowSelection={{ fixed: true, selectedRowKeys: selected, onChange: keys => setSelected(keys) }}
          onRow={row => ({
            onClick: event => {
              if (event.target.closest(".ant-table-selection-column, .ant-dropdown, .ant-btn")) return;
              openEditor(row);
            },
          })}
          onChange={(pagination, filters, sorter) => {
            const next = sorter?.order && ORDERING[sorter.columnKey] ? { key: sorter.columnKey, dir: sorter.order } : null;
            if (JSON.stringify(next) === JSON.stringify(sort)) return;
            setSort(next);
            setPage(1);
          }}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Flex vertical gap={4}>
                    <Typography.Text strong>{t("empty_filtered")}</Typography.Text>
                    <Typography.Text type="secondary">{t("empty")}</Typography.Text>
                  </Flex>
                }
              />
            ),
          }}
        />

        <TableFoot
          id="pt"
          page={page}
          size={size}
          total={total}
          sizes={PAGE_SIZES}
          labels={{ perPage: tv("rows_per_page"), range: tv("range_of") }}
          onPage={next => {
            setPage(next);
            setSelected([]);
          }}
          onSize={next => {
            setSize(next);
            setPage(1);
            setSelected([]);
          }}
        />
      </Card>

      {selected.length > 0 && (
        <Flex className="lp-bulkbar" align="center" gap="middle" wrap>
          <Typography.Text className="lp-bulkbar__n">{fill(tp("w_selected"), { n: selected.length })}</Typography.Text>
          <Space size={8}>
            <Button size="small" onClick={() => setSelected([])}>
              {tp("clear_selection")}
            </Button>
            {/* Put the selection in a point group. It carries the whole selection with it, so it
                takes the rows, the live query (for "add all matching") and the match count. */}
            <AddToGroup
              points={targets}
              query={query}
              total={total}
              currentUser={me}
              onDone={() => {
                setSelected([]);
                reload();
              }}
            />
            {segment === "archived" ? (
              <Button size="small" icon={<UndoOutlined />} loading={busy} onClick={() => confirmArchive(targets, true)}>
                {t("restore_btn")}
              </Button>
            ) : (
              <Button size="small" danger type="primary" icon={<InboxOutlined />} loading={busy} onClick={() => confirmArchive(targets, false)}>
                {t("archive_btn")}
              </Button>
            )}
          </Space>
        </Flex>
      )}
    </Flex>
  );
};

export default List;
